import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { emailLogApi } from "@/lib/email-log";

export interface EmailMessageViewerDialogProps {
  emailLogId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional resend affordance. When provided AND the loaded message failed
   * to send, a "Resend (body only)" button is rendered in the dialog footer.
   * Callers that omit this prop get no button rendered.
   */
  onResend?: (emailLogId: number) => Promise<void>;
}

/**
 * Read-only viewer for a single sent email, sourced from EmailLog. Shared by
 * the Sent Email page and the admin Email Log page — both carry an
 * `emailLogId` that resolves to the same record.
 */
export function EmailMessageViewerDialog({ emailLogId, open, onOpenChange, onResend }: EmailMessageViewerDialogProps) {
  const { data: msg, isLoading, isError } = useQuery({
    queryKey: ["email-log", emailLogId],
    queryFn: () => emailLogApi.get(emailLogId!),
    enabled: open && emailLogId != null,
  });

  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // A single persistent dialog instance is reused across rows (the admin
  // EmailLogPage only toggles `emailLogId`), so reset transient resend state
  // whenever the opened message changes — otherwise a stale error banner or
  // pending flag leaks onto the next message.
  useEffect(() => {
    setResendError(null);
    setResending(false);
  }, [emailLogId]);

  const attachments = msg?.attachmentFileNames
    ? msg.attachmentFileNames.split(", ").filter(Boolean)
    : [];

  const canResend = onResend != null && msg != null && msg.sentSuccessfully === false;

  const handleResend = async () => {
    if (!onResend || emailLogId == null) return;
    setResendError(null);
    setResending(true);
    try {
      await onResend(emailLogId);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : "Failed to resend message.");
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{msg?.subject ?? "Message"}</DialogTitle>
        </DialogHeader>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {isError && <p role="alert" className="text-sm text-destructive">Failed to load message.</p>}

        {msg && (
          <div className="space-y-3">
            <div className="space-y-1 text-sm">
              {msg.fromEmailAddress && (
                <div><span className="text-muted-foreground">From:</span> {msg.fromEmailAddress}</div>
              )}
              <div><span className="text-muted-foreground">To:</span> {msg.toEmailAddress}</div>
              {msg.cc && (
                <div><span className="text-muted-foreground">Cc:</span> {msg.cc}</div>
              )}
              {msg.bcc && (
                <div><span className="text-muted-foreground">Bcc:</span> {msg.bcc}</div>
              )}
              <div>
                <span className="text-muted-foreground">Sent:</span>{" "}
                {new Date(msg.sentAtUtc).toLocaleString("en-AU")}
              </div>
            </div>

            {!msg.sentSuccessfully && msg.errorMessage && (
              <p className="text-sm text-destructive">Failed: {msg.errorMessage}</p>
            )}

            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((name) => (
                  <Badge key={name} variant="outline">{name}</Badge>
                ))}
              </div>
            )}

            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.htmlBody) }}
            />

            {resendError && (
              <p role="alert" className="text-sm text-destructive">{resendError}</p>
            )}
          </div>
        )}

        {canResend && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={resending}
              onClick={() => void handleResend()}
            >
              {resending ? "Resending…" : "Resend (body only)"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
